/**
 * Labor-market measures as the Bureau of Labor Statistics defines them for the Current
 * Population Survey (CPS).
 *
 * Everyone aged 16 and over in the civilian noninstitutional population is employed,
 * unemployed, or not in the labor force. The labor force is the employed plus the
 * unemployed; the official unemployment rate (U-3) is the unemployed ÷ the labor force.
 */

/** Which third quantity the reader supplied besides employed and unemployed. */
export type Rest = "population" | "not-in-labor-force";

export interface LaborStats {
  employed: number;
  unemployed: number;
  laborForce: number;
  notInLaborForce: number;
  population: number;
  /** U-3: unemployed ÷ labor force, as a decimal. */
  unemploymentRate: number;
  /** Labor force ÷ population. */
  participationRate: number;
  /** Employed ÷ population. */
  employmentPopulationRatio: number;
}

export type LaborOutcome =
  | LaborStats
  | "invalid"
  | "empty-labor-force"
  | "population-too-small";

export function laborStats(
  employed: number,
  unemployed: number,
  third: number,
  rest: Rest
): LaborOutcome {
  if (![employed, unemployed, third].every(Number.isFinite)) {
    return "invalid";
  }
  if (employed < 0 || unemployed < 0 || third < 0) {
    return "invalid";
  }
  const laborForce = employed + unemployed;
  if (laborForce === 0) {
    return "empty-labor-force";
  }
  const population = rest === "population" ? third : laborForce + third;
  if (population < laborForce) {
    return "population-too-small";
  }
  return {
    employed,
    unemployed,
    laborForce,
    notInLaborForce: population - laborForce,
    population,
    unemploymentRate: unemployed / laborForce,
    participationRate: laborForce / population,
    employmentPopulationRatio: employed / population,
  };
}

/** The change from `before` to `after` in percentage points, from two decimal rates. */
export function pointChange(before: number, after: number): number {
  return (after - before) * 100;
}
